<script setup lang="ts">
import { computed } from 'vue'
import type { CSSProperties } from 'vue'

import type { TableDensity } from './types'

import Tooltip from '@/shared/ui/tooltip/Tooltip.vue'

const props = defineProps<{
  align: 'start' | 'center' | 'end'
  numeric: boolean
  frozen: boolean
  cellStyle: CSSProperties
  tooltip: string | undefined
  onlyOnOverflow: boolean
  density: TableDensity
  wrap?: boolean
  striped?: boolean
  supplementary?: boolean
}>()

const contentAlignClass = computed(() => ({
  'text-start justify-start': props.align === 'start',
  'text-center justify-center': props.align === 'center',
  'text-end justify-end': props.align === 'end',
}))
</script>

<template>
  <td
    class="ui-table-td group/td after:absolute after:inset-y-0 after:end-0 after:w-px after:bg-[var(--ui-table-grid)] after:content-[''] last:after:hidden px-md py-xs text-start text-[length:var(--ui-table-cell-size)] leading-[var(--ui-table-cell-line-height)] text-[color:var(--ui-table-cell-fg)]"
    :class="[
      `ui-table-td--${align}`,
      `ui-table-td--${density}`,
      {
        'text-center': align === 'center',
        'text-end': align === 'end',
        'ui-table-td--numeric font-numeric tabular-nums': numeric,
        relative: !frozen,
        'ui-table-td--frozen sticky z-floating': frozen,
        'ui-table-td--supplementary bg-[color:var(--ui-table-supplementary-bg)]': supplementary,
        'bg-[var(--ui-table-row-alt-bg)]': striped && !supplementary,
        'bg-[var(--ui-table-row-bg)]': !striped && !supplementary,
        'px-md py-xs text-xs': density === 'compact',
      },
    ]"
    :style="cellStyle"
  >
    <Tooltip v-if="tooltip" :text="tooltip" :only-on-overflow="onlyOnOverflow">
      <span
        class="ui-table-td__content flex min-w-0 overflow-hidden text-ellipsis [-webkit-box-orient:vertical] [-webkit-line-clamp:2] wrap-anywhere"
        :class="[
          contentAlignClass,
          {
            'ui-table-td__content--wrap flex break-words text-clip [-webkit-line-clamp:unset]':
              wrap,
          },
        ]"
      >
        <slot />
      </span>
    </Tooltip>
    <span
      v-else
      class="ui-table-td__content flex min-w-0 overflow-hidden text-ellipsis [-webkit-box-orient:vertical] [-webkit-line-clamp:2] wrap-anywhere"
      :class="[
        contentAlignClass,
        {
          'ui-table-td__content--wrap flex break-words text-clip [-webkit-line-clamp:unset]': wrap,
        },
      ]"
    >
      <slot />
    </span>
  </td>
</template>
